import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, NotificationsActive } from '@mui/icons-material';

import { PageIndices, TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { AdaptiveTabs, AdaptiveTabItem, useAdaptiveTab } from '@components/common/AdaptiveTabs.tsx';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { toastService } from '@services/toast.service.ts';
import { getAdminNotifications, createAdminNotification, GetNotificationsParams } from '@services/notification.service.ts';
import { NotificationCatalogTab } from '@components/admin/notifications/NotificationCatalogTab.tsx';
import { AdminNotification, CreateAdminNotificationDto } from '@/types/notification.ts';

const getChipColor = (level: 'info' | 'warning' | 'danger') => {
  switch (level) {
    case 'info':    return 'info';
    case 'warning': return 'warning';
    case 'danger':  return 'error';
    default:        return 'default';
  }
};

const TABS: AdaptiveTabItem[] = [
  { value: 'list', label: 'Сповіщення', iDoc: TabIndices.NOTIFICATIONS_LIST },
  { value: 'mailing', label: 'Розсилки', iDoc: TabIndices.NOTIFICATIONS_MAILING },
  { value: 'catalog', label: 'Каталог', iDoc: TabIndices.ADMIN_NOTIFICATIONS_CATALOG },
];

export const AdminNotificationsPage: React.FC = () => {
  const currentTab = useAdaptiveTab(TABS, { searchParamKey: 'tab', defaultValue: 'list', storageKey: 'tab:admin-notifications' });
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterLevel, setFilterLevel] = useState<'' | 'info' | 'warning' | 'danger'>('');
  const [filterStatus, setFilterStatus] = useState<'' | 'read' | 'unread'>('');

  const [formData, setFormData] = useState<CreateAdminNotificationDto>({
    userId: 0,
    message: '',
    title: '',
    level: 'info',
  });

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params: GetNotificationsParams = {
        page,
        limit,
        search: searchQuery || undefined,
        level: filterLevel || undefined,
        status: filterStatus || undefined,
      };
      const response = await getAdminNotifications(params);
      setNotifications(response.data);
      setTotal(response.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити сповіщення');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, filterLevel, filterStatus]);

  useEffect(() => {
    if (currentTab === 'list') {
      fetchNotifications();
    }
  }, [fetchNotifications, currentTab]);

  const handleOpenDialog = () => {
    setFormData({ userId: 0, message: '', title: '', level: 'info' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleInputChange = (field: keyof CreateAdminNotificationDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.userId || !formData.message) {
      toastService.error("Заповніть всі обов'язкові поля");
      return;
    }
    setSubmitting(true);
    try {
      await createAdminNotification(formData);
      toastService.success('Сповіщення надіслано');
      handleCloseDialog();
      fetchNotifications();
    } catch {
      toastService.error('Не вдалося надіслати сповіщення');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <Box data-i-doc={PageIndices.ADMIN_NOTIFICATIONS}>
        <AdaptiveTabs tabs={TABS} searchParamKey="tab" defaultValue="list" storageKey="tab:admin-notifications" />

        {currentTab === 'list' && (
          <Box data-i-doc={TabIndices.NOTIFICATIONS_LIST} sx={{ pt: 2 }}>
            <PageHint iDoc={TabIndices.NOTIFICATIONS_LIST} />
            <PageCard>
              <PageToolbar
                storageKey="filters:admin-notifications"
                filters={[
                  {
                    id: 'search',
                    type: 'text',
                    label: 'Пошук',
                    value: searchQuery,
                    onChange: (v) => { setSearchQuery(v); setPage(1); },
                    flexGrow: 1,
                  },
                  {
                    id: 'level',
                    type: 'select',
                    label: 'Рівень',
                    value: filterLevel,
                    onChange: (v) => { setFilterLevel(v); setPage(1); },
                    options: [
                      { value: 'info',    label: 'Info' },
                      { value: 'warning', label: 'Warning' },
                      { value: 'danger',  label: 'Danger' },
                    ],
                    minWidth: '140px',
                  },
                  {
                    id: 'status',
                    type: 'select',
                    label: 'Статус',
                    value: filterStatus,
                    onChange: (v) => { setFilterStatus(v); setPage(1); },
                    options: [
                      { value: 'read',   label: 'Прочитані' },
                      { value: 'unread', label: 'Непрочитані' },
                    ],
                    minWidth: '150px',
                  },
                ]}
                onReset={() => { setSearchQuery(''); setFilterLevel(''); setFilterStatus(''); setPage(1); }}
                
                extraActions={
                  <Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog}>
                    Надіслати
                  </Button>
                }
              />

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Користувач</TableCell>
                      <TableCell>Повідомлення</TableCell>
                      <TableCell>Рівень</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Дата</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : notifications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Сповіщень не знайдено
                        </TableCell>
                      </TableRow>
                    ) : (
                      notifications.map((notification) => (
                        <TableRow key={notification.id}>
                          <TableCell>{notification.id}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {notification.userName || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {notification.userEmail || `ID: ${notification.userId}`}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{notification.message}</TableCell>
                          <TableCell>
                            <Chip
                              label={notification.level}
                              color={getChipColor(notification.level)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={notification.isRead ? 'Прочитано' : 'Непрочитано'}
                              color={notification.isRead ? 'default' : 'primary'}
                              variant={notification.isRead ? 'outlined' : 'filled'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(notification.createdAt).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <UniversalPagination
                page={page}
                count={Math.ceil(total / limit)}
                limit={limit}
                total={total}
                onPageChange={setPage}
                onLimitChange={(val) => { setLimit(val); setPage(1); }}
                storageKey="pagination:admin-notifications"
              />
            </PageCard>
          </Box>
        )}

        {currentTab === 'mailing' && (
          <Box data-i-doc={TabIndices.NOTIFICATIONS_MAILING} sx={{ pt: 2 }}>
            <PageHint iDoc={TabIndices.NOTIFICATIONS_MAILING} />
            <PageCard>
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Тут будуть розсилки. Вкладка поки порожня.
                </Typography>
              </Box>
            </PageCard>
          </Box>
        )}

        {currentTab === 'catalog' && (
          <Box sx={{ pt: 2 }}>
            <NotificationCatalogTab />
          </Box>
        )}
      </Box>

      <BaseDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title="Надіслати сповіщення"
        maxWidth="sm"
        actions={
          <Button
            type="submit"
            form="notification-form"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <NotificationsActive />}
          >
            Надіслати
          </Button>
        }
      >
        <form id="notification-form" onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                label="User ID"
                type="number"
                fullWidth
                required
                value={formData.userId || ''}
                onChange={(e) => handleInputChange('userId', parseInt(e.target.value))}
                helperText="Введіть ID користувача, якому хочете надіслати сповіщення"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Рівень</InputLabel>
                <Select
                  value={formData.level}
                  label="Рівень"
                  onChange={(e) => handleInputChange('level', e.target.value)}
                >
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="danger">Danger</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Заголовок (Push)"
                fullWidth
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Сповіщення"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Повідомлення"
                fullWidth
                multiline
                rows={4}
                required
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
              />
            </Grid>
          </Grid>
        </form>
      </BaseDialog>
    </DashboardLayout>
  );
};
